import classNames from 'classnames';
import React, { ReactElement, useId, useState } from 'react';

type TooltipProps = {
	children: ReactElement; // The element to attach tooltip to (must be single element)
	text: string | React.ReactNode; // The tooltip text to display
	position?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'right'; // Tooltip position
	className?: string; // Additional classes for the wrapper
};

/**
 * Tooltip Component
 * 
 * A reusable tooltip component using CSS anchor positioning.
 * Wrap any element with this component to add a hover tooltip.
 * Supports both mouse hover and keyboard (Enter/Space to toggle, Escape to hide).
 * 
 * @example
 * ```tsx
 * <Tooltip text="This is helpful information" position="top">
 *   <InfoIconOutlined width="26" height="26" />
 * </Tooltip>
 * ```
 */
const Tooltip: React.FC<TooltipProps> = ({
	children,
	text,
	position = 'top',
	className = ''
}) => {
	// Generate unique anchor name using React's useId
	const id = useId();
	const anchorName = `--tooltip${id.replace(/:/g, '')}`;

	// State for keyboard-triggered visibility
	const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

	// Handle keyboard interactions
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setIsKeyboardOpen(!isKeyboardOpen);
		} else if (e.key === 'Escape') {
			e.preventDefault();
			setIsKeyboardOpen(false);
		}
	};

	// Hide tooltip when focus leaves the element
	const handleBlur = () => {
		setIsKeyboardOpen(false);
	};

	const ariaLabel = typeof text === 'string' ? text : undefined;

	return (
		<span
			className={classNames('tooltip-wrapper', { 'keyboard-active': isKeyboardOpen })}
			tabIndex={0}
			aria-label={ariaLabel}
			onKeyDown={handleKeyDown}
			onBlur={handleBlur}
			role="button"
		>
			<span
				className={classNames('tooltip-trigger', className)}
				style={{ ['anchorName' as any]: anchorName }}
			>
				{children}
			</span>
			<span
				className={classNames('tooltip-content', `tooltip-${position}`)}
				style={{ ['positionAnchor' as any]: anchorName }}
				role="tooltip"
			>
				{text}
			</span>
		</span>
	);
};

export default Tooltip;
